import { useEffect, useState, useCallback, useMemo, useRef, memo } from "react";
import { APIProvider, Map, Marker, InfoWindow, useApiIsLoaded, useMap } from "@vis.gl/react-google-maps";
import useWebSocket from "react-use-websocket";
import { useSocketContext } from "../../context/socket.jsx";
import { useSessionContext } from "../../context/session.context";
import { getLiveMapData, getTripOriginsData } from "../../services/liveMap";
import { getObject, getString } from "../../utils/handler_validations";
import styles from "./styles";
import { AnimatedDriverMarker } from "../../components/trips/AnimatedDriverMarker";

const TEPIC_CENTER = { lat: 21.5019, lng: -104.9026 };

const MapController = ({ center, zoom, initialCenter }) => {
  const map = useMap();
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (map && !hasInitialized.current) {
      map.setCenter(center);
      map.setZoom(zoom);
      hasInitialized.current = true;
    }
  }, [map, center, zoom]);

  useEffect(() => {
    if (map && initialCenter) {
      map.panTo(initialCenter);
    }
  }, [map, initialCenter]);

  return null;
};

const DriverInfoWindowContent = memo(({ name, tel, inTrip, activeTrip, hasWebSocket, hasPushToken }) => {
  return (
    <>
      <div className="info-header">
        <div className="info-title">{name}</div>
        <div className="info-subtitle">Conductor 🚖</div>
      </div>
      
      <div className="info-row">
        <span className="label">Teléfono:</span>
        <span className="value">{tel}</span>
      </div>
      <div className="info-row">
        <span className="label">Estado:</span>
        <span className={`badge ${inTrip ? "badge-trip" : "badge-free"}`}>
          {inTrip ? "En Viaje" : "Libre"}
        </span>
      </div>
      
      <div className="info-row">
        <span className="label">Conexión WebSocket:</span>
        <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: "600", color: hasWebSocket ? "#10B981" : "#EF4444" }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: hasWebSocket ? "#10B981" : "#EF4444", display: "inline-block" }} />
          {hasWebSocket ? "Conectado" : "Desconectado"}
        </span>
      </div>
      <div className="info-row">
        <span className="label">Notificaciones Push:</span>
        <span style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: "600", color: hasPushToken ? "#10B981" : "#EF4444" }}>
          <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: hasPushToken ? "#10B981" : "#EF4444", display: "inline-block" }} />
          {hasPushToken ? "Activo (Token OK)" : "Inactivo (Sin Token)"}
        </span>
      </div>
      
      {inTrip && activeTrip && (
        <div className="section-divider">
          <div className="section-title">Pasajero En Curso</div>
          <div className="info-row" style={{ borderBottom: "none", padding: "4px 0" }}>
            <span className="label" style={{ fontWeight: "normal", color: "#4a5568" }}>{activeTrip.passengerName}</span>
            <span className="value" style={{ fontSize: 11, color: "#718096" }}>{activeTrip.passengerTel}</span>
          </div>
          <div className="info-row" style={{ borderBottom: "none", padding: "4px 0" }}>
            <span className="label" style={{ fontWeight: "normal", color: "#718096" }}>Origen:</span>
            <span className="value" style={{ fontSize: 10, color: "#a0aec0", fontWeight: "normal" }}>
              ({activeTrip.origin.lat.toFixed(5)}, {activeTrip.origin.lon.toFixed(5)})
            </span>
          </div>
          <div className="info-row" style={{ borderBottom: "none", padding: "4px 0" }}>
            <span className="label" style={{ fontWeight: "normal", color: "#718096" }}>Destino:</span>
            <span className="value" style={{ fontSize: 10, color: "#a0aec0", fontWeight: "normal" }}>
              ({activeTrip.destination.lat.toFixed(5)}, {activeTrip.destination.lon.toFixed(5)})
            </span>
          </div>
        </div>
      )}
    </>
  );
});

const TripInfoWindowContent = memo(({ id, passengerName, passengerTel, cost, distance, duration, driverName, estatus }) => {
  return (
    <>
      <div className="info-header">
        <div className="info-title">Viaje #{id}</div>
        <div className="info-subtitle">Pasajero: {passengerName}</div>
      </div>
      
      <div className="info-row">
        <span className="label">Teléfono Pasajero:</span>
        <span className="value">{passengerTel}</span>
      </div>
      <div className="info-row">
        <span className="label">Costo:</span>
        <span className="value">${cost} MXN</span>
      </div>
      <div className="info-row">
        <span className="label">Distancia / Tiempo:</span>
        <span className="value">
          {Number(distance || 0).toFixed(1)} km / {Number(duration || 0).toFixed(1)} min
        </span>
      </div>
      {driverName && (
        <div className="info-row">
          <span className="label">Conductor:</span>
          <span className="value">{driverName}</span>
        </div>
      )}
      <div className="info-row">
        <span className="label">Estado Viaje:</span>
        <span className={`badge ${estatus === 1 ? "badge-waiting" : "badge-trip"}`}>
          {estatus === 1 ? "Esperando Taxi" : "En Curso"}
        </span>
      </div>
    </>
  );
});

const HIDE_LABELS_STYLE = [
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }]
  },
  {
    featureType: "transit",
    elementType: "labels",
    stylers: [{ visibility: "off" }]
  },
  {
    featureType: "road",
    elementType: "labels",
    stylers: [{ visibility: "off" }]
  }
];

const MonitoreoContent = () => {
  const session = useSessionContext();
  const socketCtx = useSocketContext();
  const isApiLoaded = useApiIsLoaded();
  const [initialCenter, setInitialCenter] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null); // { type: 'driver'|'trip'|'demand', data }
  const [loading, setLoading] = useState(true);
  const [hideMapLabels, setHideMapLabels] = useState(false);

  // Estados para el mapa de demanda histórico
  const [activeTab, setActiveTab] = useState("live"); // 'live' | 'demand'
  const [demandPeriod, setDemandPeriod] = useState("day"); // 'hour' | 'day' | 'week' | 'month'
  const [demandStatus, setDemandStatus] = useState("all"); // 'all' | 'completed' | 'canceled' | 'active'
  const [demandOrigins, setDemandOrigins] = useState([]);
  const [loadingDemand, setLoadingDemand] = useState(false);

  const fetchDemandData = useCallback(async () => {
    try {
      setLoadingDemand(true);
      const resp = await getTripOriginsData(demandPeriod, demandStatus, session.value.jwt);
      if (!resp.error && resp.body) {
        setDemandOrigins(resp.body.origins || []);
      }
    } catch (err) {
      console.error("Error loading demand origins:", err.message);
    } finally {
      setLoadingDemand(false);
    }
  }, [demandPeriod, demandStatus, session.value.jwt]);

  useEffect(() => {
    if (activeTab === "demand") {
      fetchDemandData();
    }
  }, [activeTab, fetchDemandData]);


  // Obtener ubicación actual del navegador
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          setInitialCenter({ lat, lng: lon });
        },
        (error) => {
          console.error("Error al obtener la ubicación actual:", error.message);
        }
      );
    }
  }, []);

  // Cargar datos iniciales (isSilent evita mostrar la pantalla de carga durante sincronizaciones en segundo plano)
  const loadInitialData = useCallback(async (isSilent = false) => {
    try {
      if (!isSilent) setLoading(true);
      const resp = await getLiveMapData(session.value.jwt);
      if (!resp.error && resp.body) {
        setDrivers(resp.body.drivers || []);
        setTrips(resp.body.trips || []);
      }
    } catch (err) {
      console.error("Error loading live map data:", err.message);
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [session.value.jwt]);

  useEffect(() => {
    loadInitialData(false); // Carga inicial con spinner
  }, [loadInitialData]);

  // Polling de sincronización periódica (cada 20s) silenciosa en segundo plano
  useEffect(() => {
    if (activeTab !== "live") return;
    const interval = setInterval(() => {
      loadInitialData(true); // Carga silenciosa sin desmontar el mapa
    }, 20000);
    return () => clearInterval(interval);
  }, [loadInitialData, activeTab]);

  // Conectar al canal WebSocket usando share: true
  const { lastJsonMessage } = useWebSocket(socketCtx.url, {
    share: true,
    shouldReconnect: () => true
  }, !!socketCtx.url);

  // Escuchar actualizaciones WebSocket en tiempo real
  useEffect(() => {
    if (!lastJsonMessage) return;

    const data = getObject(lastJsonMessage);
    const type = getString(data.responseType);

    if (type === "DRIVER_UPDATE_POSITION") {
      // Actualizar posición de conductor en tiempo real
      setDrivers(prevDrivers => {
        const index = prevDrivers.findIndex(d => d.id === data.driverId);
        if (index !== -1) {
          const updated = [...prevDrivers];
          const latVal = parseFloat(data.coords?.latitude || data.coords?.lat);
          const lonVal = parseFloat(data.coords?.longitude || data.coords?.lon);
          updated[index] = {
            ...updated[index],
            coords: {
              lat: !isNaN(latVal) ? latVal : updated[index].coords.lat,
              lon: !isNaN(lonVal) ? lonVal : updated[index].coords.lon
            },
            heading: data.heading ?? updated[index].heading
          };
          return updated;
        } else {
          // Si es un conductor nuevo que acaba de entrar en línea
          loadInitialData(true); // Carga silenciosa para evitar parpadeos
        }
        return prevDrivers;
      });

      // No actualizamos selectedElement para cambios de coordenadas del conductor,
      // evitando re-renderizados innecesarios y microparpadeos del tooltip InfoWindow.
    }

    if (type === "DRIVER_STATUS_UPDATE") {
      const driverId = data.driverId;
      const isOnline = data.isOnline;

      setDrivers(prevDrivers => {
        if (!isOnline) {
          // Si el conductor se desconectó, lo removemos del mapa inmediatamente
          return prevDrivers.filter(d => d.id !== driverId);
        } else {
          // Si el conductor se conectó y no lo tenemos en la lista, recargamos
          if (!prevDrivers.some(d => d.id === driverId)) {
            loadInitialData(true); // Carga silenciosa para evitar parpadeos
          } else {
            // Sincronizar estado inTrip
            return prevDrivers.map(d => {
              if (d.id === driverId) {
                return { ...d, inTrip: data.inTrip === 1 };
              }
              return d;
            });
          }
        }
        return prevDrivers;
      });

      // Sincronizar selectedElement
      setSelectedElement(prev => {
        if (prev && prev.type === "driver" && prev.data.id === driverId) {
          if (!isOnline) return null;
          return {
            ...prev,
            data: {
              ...prev.data,
              inTrip: data.inTrip === 1
            }
          };
        }
        return prev;
      });
    }

    if (type === "TRIP_CREATED") {
      // Agregar nuevo viaje en espera al mapa
      setTrips(prevTrips => {
        if (prevTrips.some(t => t.id === data.id)) return prevTrips;
        return [...prevTrips, data];
      });
    }

    if (type === "TRIP_UPDATED") {
      const tripId = Number(data.tripId);
      const estatus = Number(data.estatus);

      setTrips(prevTrips => {
        // Encontrar conductor asignado antes de filtrar o remover el viaje
        const existingTrip = prevTrips.find(t => Number(t.id) === tripId);
        const assignedDriverId = data.driverId || (existingTrip ? existingTrip.driverId : null);

        if (assignedDriverId) {
          setDrivers(prevDrivers => prevDrivers.map(d => {
            if (Number(d.id) === Number(assignedDriverId)) {
              return {
                ...d,
                inTrip: estatus >= 2 && estatus < 5 // 2, 3, 4 significa que está en viaje activo
              };
            }
            return d;
          }));

          // Sincronizar selectedElement del conductor si está abierto
          setSelectedElement(prev => {
            if (prev && prev.type === "driver" && Number(prev.data.id) === Number(assignedDriverId)) {
              return {
                ...prev,
                data: {
                  ...prev.data,
                  inTrip: estatus >= 2 && estatus < 5
                }
              };
            }
            return prev;
          });
        }

        // Si el viaje se cancela (7) o finaliza (5), remover del mapa
        if (estatus === 5 || estatus === 7) {
          return prevTrips.filter(t => Number(t.id) !== tripId);
        }
        // Si se acepta o cambia de estado, actualizar estatus y conductor
        return prevTrips.map(t => {
          if (Number(t.id) === tripId) {
            return {
              ...t,
              estatus,
              driverId: data.driverId || t.driverId,
              driverName: data.driverName || t.driverName
            };
          }
          return t;
        });
      });

      // Si tenemos abierto el InfoWindow de este viaje, actualizar o cerrar
      setSelectedElement(prev => {
        if (prev && prev.type === "trip" && Number(prev.data.id) === tripId) {
          if (estatus === 5 || estatus === 7) {
            return null;
          }
          return {
            ...prev,
            data: {
              ...prev.data,
              estatus,
              driverName: data.driverName || prev.data.driverName
            }
          };
        }
        return prev;
      });
    }
  }, [lastJsonMessage, loadInitialData]);



  const passengerIcon = useMemo(() => ({
    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36">
        <path fill="#F7AF36" stroke="#FFFFFF" stroke-width="1.5" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
      </svg>
    `),
    scaledSize: { width: 36, height: 36 },
    anchor: { x: 18, y: 36 }
  }), []);

  const tripOnGoingIcon = useMemo(() => ({
    url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
    scaledSize: { width: 32, height: 32 }
  }), []);

  const getDemandPin = useCallback((status) => {
    let color = "#F7AF36"; // Orange/Yellow for Active (estatus IN 1,2,3,4)
    if (status === 5) {
      color = "#16CA5E"; // Green for Completed
    } else if (status === 6 || status === 7) {
      color = "#DC2626"; // Red for Canceled/Expired
    }
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
          <path fill="${color}" stroke="#FFFFFF" stroke-width="1.5" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
        </svg>
      `),
      scaledSize: { width: 32, height: 32 },
      anchor: { x: 16, y: 32 }
    };
  }, []);

  // Conteos estadísticos para la cabecera
  const stats = useMemo(() => {
    const totalDrivers = drivers.length;
    const busyDrivers = drivers.filter(d => d.inTrip).length;
    const freeDrivers = totalDrivers - busyDrivers;
    const waitingTrips = trips.filter(t => t.estatus === 1).length;
    const activeTrips = trips.filter(t => t.estatus > 1 && t.estatus < 5).length;
    return { totalDrivers, busyDrivers, freeDrivers, waitingTrips, activeTrips };
  }, [drivers, trips]);

  return (
    <styles.DivContainer>
      <styles.Header style={{ padding: "10px 16px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: "20px" }}>
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexShrink: 0 }}>
          <button 
            onClick={() => {
              setActiveTab("live");
              setSelectedElement(null);
            }}
            style={{
              padding: "6px 14px",
              borderRadius: "6px",
              border: activeTab === "live" ? "1px solid #111827" : "1px solid #d1d5db",
              fontWeight: "700",
              fontSize: "14px",
              backgroundColor: activeTab === "live" ? "#111827" : "#ffffff",
              color: activeTab === "live" ? "#ffffff" : "#374151",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            Monitoreo en Vivo
          </button>
          <button 
            onClick={() => {
              setActiveTab("demand");
              setSelectedElement(null);
            }}
            style={{
              padding: "6px 14px",
              borderRadius: "6px",
              border: activeTab === "demand" ? "1px solid #111827" : "1px solid #d1d5db",
              fontWeight: "700",
              fontSize: "14px",
              backgroundColor: activeTab === "demand" ? "#111827" : "#ffffff",
              color: activeTab === "demand" ? "#ffffff" : "#374151",
              cursor: "pointer",
              transition: "all 0.2s"
            }}
          >
            Historial de Demanda
          </button>
        </div>

        {activeTab === "live" ? (
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap", justifyContent: "flex-end", flexGrow: 1 }}>
            <div style={{ fontSize: "13px", color: "#374151" }}>
              Conductores: <strong style={{ color: "#111827" }}>{stats.totalDrivers}</strong> (Libres: {stats.freeDrivers} | Ocupados: {stats.busyDrivers})
            </div>
            <div style={{ fontSize: "13px", color: "#b45309", backgroundColor: "#fef3c7", padding: "4px 8px", borderRadius: "4px" }}>
              En espera: <strong style={{ color: "#78350f" }}>{stats.waitingTrips}</strong>
            </div>
            <div style={{ fontSize: "13px", color: "#15803d", backgroundColor: "#dcfce7", padding: "4px 8px", borderRadius: "4px" }}>
              En curso: <strong style={{ color: "#166534" }}>{stats.activeTrips}</strong>
            </div>
            <button 
              onClick={() => setHideMapLabels(!hideMapLabels)}
              style={{
                padding: "6px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                backgroundColor: hideMapLabels ? "#374151" : "#ffffff",
                color: hideMapLabels ? "#ffffff" : "#374151",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "600",
                marginLeft: "8px"
              }}
            >
              {hideMapLabels ? "Mostrar Etiquetas" : "Ocultar Etiquetas"}
            </button>
          </div>
        ) : (
          <div style={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
            flexGrow: 1,
            justifyContent: "space-between"
          }}>
            <div style={{ display: "flex", gap: "16px", flexGrow: 1, justifyContent: "center", maxWidth: "600px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                <span style={{ fontSize: "12px", fontWeight: "700", color: "#4b5563", whiteSpace: "nowrap" }}>Periodo:</span>
                <select 
                  value={demandPeriod} 
                  onChange={(e) => setDemandPeriod(e.target.value)}
                  style={{ padding: "5px 10px", borderRadius: "6px", border: "1px solid #d1d5db", backgroundColor: "#ffffff", fontSize: "13px", width: "100%", height: "32px" }}
                >
                  <option value="hour">Última hora</option>
                  <option value="day">Hoy (Últimas 24h)</option>
                  <option value="week">Esta semana (7 días)</option>
                  <option value="month">Este mes (30 días)</option>
                </select>
              </div>
              
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: 1 }}>
                <span style={{ fontSize: "12px", fontWeight: "700", color: "#4b5563", whiteSpace: "nowrap" }}>Estatus:</span>
                <select 
                  value={demandStatus} 
                  onChange={(e) => setDemandStatus(e.target.value)}
                  style={{ padding: "5px 10px", borderRadius: "6px", border: "1px solid #d1d5db", backgroundColor: "#ffffff", fontSize: "13px", width: "100%", height: "32px" }}
                >
                  <option value="all">Todos los viajes</option>
                  <option value="completed">Solo completados</option>
                  <option value="canceled">Solo cancelados/expirados</option>
                  <option value="active">Solo activos/en curso</option>
                </select>
              </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px", flexShrink: 0 }}>
              <button 
                onClick={() => setHideMapLabels(!hideMapLabels)}
                style={{
                  padding: "6px 12px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  backgroundColor: hideMapLabels ? "#374151" : "#ffffff",
                  color: hideMapLabels ? "#ffffff" : "#374151",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "600"
                }}
              >
                {hideMapLabels ? "Mostrar Etiquetas" : "Ocultar Etiquetas"}
              </button>

              <div style={{ fontSize: "13px", fontWeight: "600", color: "#374151", backgroundColor: "#eff6ff", padding: "6px 12px", borderRadius: "6px", border: "1px solid #bfdbfe" }}>
                {loadingDemand ? (
                  <span style={{ color: "#6b7280" }}>Cargando...</span>
                ) : (
                  <>Orígenes: <strong style={{ color: "#2563eb", fontSize: "14px" }}>{demandOrigins.length}</strong></>
                )}
              </div>
            </div>
          </div>
        )}
      </styles.Header>

      <div style={{ width: "100%", height: "calc(100vh - 120px)", minHeight: "560px", position: "relative" }}>
        {isApiLoaded && !loading ? (
          <Map
            style={{ width: "100%", height: "100%" }}
            styles={hideMapLabels ? HIDE_LABELS_STYLE : []}
            options={{
              fullscreenControl: true,
              streetViewControl: false,
              mapTypeControl: true,
            }}
          >
            <MapController center={TEPIC_CENTER} zoom={13} initialCenter={initialCenter} />
            
            {activeTab === "live" ? (
              <>
                {/* Marcadores de Conductores (Tiempo Real) */}
                {drivers.map(driver => (
                  <AnimatedDriverMarker
                    key={`driver-${driver.id}`}
                    driver={driver}
                    onClick={(currentPos) => setSelectedElement({
                      type: "driver",
                      data: driver,
                      anchorCoords: currentPos ? { lat: currentPos.lat, lng: currentPos.lng } : { lat: driver.coords.lat, lng: driver.coords.lon }
                    })}
                  />
                ))}

                 {/* Marcadores de Viajes (Solo en espera/despacho, ocultar cuando ya están en curso/estatus 4) */}
                {trips.filter(trip => Number(trip.estatus) < 4).map(trip => {
                  const isWaiting = trip.estatus === 1;
                  return (
                    <Marker
                      key={`trip-${trip.id}`}
                      position={{ lat: trip.origin.lat, lng: trip.origin.lon }}
                      icon={isWaiting ? passengerIcon : tripOnGoingIcon}
                      title={`Viaje #${trip.id} - ${trip.passengerName}`}
                      onClick={() => setSelectedElement({
                        type: "trip",
                        data: trip,
                        anchorCoords: { lat: trip.origin.lat, lng: trip.origin.lon }
                      })}
                    />
                  );
                })}
              </>
            ) : (
              <>
                {/* Marcadores de Origen del Histórico de Demanda */}
                {demandOrigins.map(origin => {
                  return (
                    <Marker
                      key={`demand-${origin.id}`}
                      position={{ lat: origin.lat, lng: origin.lon }}
                      icon={getDemandPin(origin.status)}
                      title={`Viaje #${origin.id}`}
                      onClick={() => setSelectedElement({
                        type: "demand",
                        data: origin,
                        anchorCoords: { lat: origin.lat, lng: origin.lon }
                      })}
                    />
                  );
                })}
              </>
            )}

            {/* InfoWindow (Tooltip) del elemento seleccionado */}
            {selectedElement && (
              <InfoWindow
                position={selectedElement.anchorCoords}
                onCloseClick={() => setSelectedElement(null)}
              >
                <styles.CustomInfoWindow style={{ padding: "4px" }}>
                  {selectedElement.type === "driver" && (
                    (() => {
                      const activeTrip = trips.find(t => 
                        (t.driverName === selectedElement.data.name || Number(t.driverId) === Number(selectedElement.data.id)) && 
                        Number(t.estatus) === 4
                      );
                      return (
                        <DriverInfoWindowContent
                          name={selectedElement.data.name}
                          tel={selectedElement.data.tel}
                          inTrip={selectedElement.data.inTrip}
                          hasWebSocket={selectedElement.data.hasWebSocket}
                          hasPushToken={selectedElement.data.hasPushToken}
                          activeTrip={activeTrip}
                        />
                      );
                    })()
                  )}
                  
                  {selectedElement.type === "trip" && (
                    <TripInfoWindowContent
                      id={selectedElement.data.id}
                      passengerName={selectedElement.data.passengerName}
                      passengerTel={selectedElement.data.passengerTel}
                      cost={selectedElement.data.cost}
                      distance={selectedElement.data.distance}
                      duration={selectedElement.data.duration}
                      driverName={selectedElement.data.driverName}
                      estatus={selectedElement.data.estatus}
                    />
                  )}

                  {selectedElement.type === "demand" && (
                    <div style={{ padding: "8px 12px", fontFamily: "inherit" }}>
                      <div style={{ fontWeight: "700", fontSize: "13px", color: "#111827", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
                        📍 Origen Viaje #{selectedElement.data.id}
                      </div>
                      <div style={{ fontSize: "11px", color: "#4b5563", marginBottom: "3px" }}>
                        <strong>Fecha:</strong> {new Date(selectedElement.data.date).toLocaleString("es-MX", { timeZone: "America/Mazatlan" })}
                      </div>
                      <div style={{ fontSize: "11px", color: "#4b5563" }}>
                        <strong>Estatus:</strong> {
                          (() => {
                            const statusLabels = {
                              1: "Esperando conductor",
                              2: "Conductor aceptó",
                              3: "Conductor llegó",
                              4: "En curso",
                              5: "Completado exitosamente 🟢",
                              6: "Cancelado por pasajero 🔴",
                              7: "Expirado / Cancelado por conductor 🔴"
                            };
                            return statusLabels[selectedElement.data.status] || "Desconocido";
                          })()
                        }
                      </div>
                    </div>
                  )}
                </styles.CustomInfoWindow>
              </InfoWindow>
            )}
          </Map>
        ) : (
          <div style={{ padding: 40, textAlign: "center", color: "#718096" }}>
            Cargando mapa...
          </div>
        )}
      </div>
    </styles.DivContainer>
  );
};

const MonitoreoPage = () => {
  return (
    <APIProvider
      apiKey={process.env.REACT_APP_MAPS_API}
      libraries={["places", "drawing"]}
    >
      <MonitoreoContent />
    </APIProvider>
  );
};

export default MonitoreoPage;
